import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Context } from '../../../store';
import axios from 'axios';

// Используем новые SVG баннеры
import myata_bg from '../../../images/icons/myata_banner.svg';
import gold_bg from '../../../images/icons/gold_banner.svg';
import xtra_bg from '../../../images/icons/xtra_banner.svg';

// Cache-busting: обновляется каждый час (обложки видны вскоре после обновления)
const getCacheBuster = () => {
    const now = new Date();
    // Используем дату и час (YYYYMMDDHH) для ежечасного сброса кеша
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hour = String(now.getHours()).padStart(2, '0');
    return `v=${year}${month}${day}${hour}`;
};

const ITEMS_PER_PAGE = 10;

const HomeScreen = ({ onNavigate }) => {
    const [state, dispatch] = useContext(Context);
    const [allPlaylists, setAllPlaylists] = useState([]);
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
    const [loading, setLoading] = useState(false);
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);

    const streams = [
        { id: 'myata', name: 'MYATA', banner: myata_bg },
        { id: 'gold', name: 'GOLD', banner: gold_bg },
        { id: 'myata_hits', name: 'XTRA', banner: xtra_bg },
    ];

    // Загрузка плейлистов из файла
    useEffect(() => {
        const fetchPlaylists = async () => {
            try {
                const playlistsUrl = window.location.hostname === 'localhost'
                    ? 'https://radiomyata.ru/covers/playlists.txt'
                    : '/covers/playlists.txt';

                const response = await axios.get(`${playlistsUrl}?${getCacheBuster()}`);
                const text = response.data;

                const lines = text.split('\n').filter(line => line.trim());
                const parsedPlaylists = lines.map(line => {
                    const parts = line.split(/\s*[—-]\s*/);
                    if (parts.length >= 2) {
                        return {
                            image: parts[0].trim(),
                            url: parts[1].trim()
                        };
                    }
                    return null;
                }).filter(Boolean);

                setAllPlaylists(parsedPlaylists);
            } catch (error) {
                console.error('Error loading playlists:', error);
                setAllPlaylists([
                    { image: 'https://radiomyata.ru/covers/xtra_classic.png', url: 'https://band.link/xtra_classix' },
                    { image: 'https://radiomyata.ru/covers/xtra.png', url: 'https://band.link/xtra' },
                    { image: 'https://radiomyata.ru/covers/2024.png', url: 'https://open.spotify.com/playlist/1bRSfZxarMftXBIgsmk830' },
                ]);
            }
        };

        fetchPlaylists();
    }, []);

    // Intersection Observer для lazy loading
    const loadMore = useCallback(() => {
        if (loading) return;
        if (visibleCount >= allPlaylists.length) return;

        setLoading(true);
        // Небольшая задержка для плавности
        setTimeout(() => {
            setVisibleCount(prev => Math.min(prev + ITEMS_PER_PAGE, allPlaylists.length));
            setLoading(false);
        }, 100);
    }, [loading, visibleCount, allPlaylists.length]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        observerRef.current = observer;

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [loadMore]);

    // Видимые плейлисты
    const visiblePlaylists = allPlaylists.slice(0, visibleCount);
    const hasMore = visibleCount < allPlaylists.length;

    const handleStreamClick = (streamId) => {
        // If already playing this stream, just navigate
        if (state.stream === streamId && state.nowPlaying) {
            onNavigate('player');
            return;
        }

        dispatch({ stream: streamId });
        dispatch({ isLoading: true });

        const audio = state.audio;
        audio.src = 'https://radio.dline-media.com/' + streamId;
        audio.play()
            .then(() => {
                dispatch({ nowPlaying: true });
                dispatch({ isLoading: false });
            })
            .catch((err) => {
                console.error('Auto-play error:', err);
                dispatch({ isLoading: false });
            });

        onNavigate('player');
    };

    return (
        <div className="mobile-screen home-screen">
            <h1 className="home-title">Наши потоки</h1>

            {/* Баннеры потоков */}
            <div className="streams-scroll">
                <div className="streams-container">
                    {streams.map((stream) => (
                        <div
                            key={stream.id}
                            className={`stream-banner ${state.stream === stream.id ? 'active' : ''}`}
                            onClick={() => handleStreamClick(stream.id)}
                        >
                             <img src={stream.banner} alt={stream.name} loading="eager" />
                        </div>
                    ))}
                    <div className="scroll-spacer" style={{ width: '15px', flexShrink: 0 }}></div>
                </div>
            </div>

            <h2 className="playlists-title">Мятные плейлисты</h2>

            {/* Плейлисты с lazy loading */}
            <div className="playlists-scroll">
                <div className="playlists-container">
                    {visiblePlaylists.map((playlist, index) => (
                        <a
                            key={index}
                            href={playlist.url}
                            target="_blank"
                            rel="noreferrer"
                            className="playlist-item"
                        >
                            <img
                                src={`${playlist.image}?${getCacheBuster()}`}
                                alt={`Playlist ${index + 1}`}
                                loading="lazy"
                            />
                        </a>
                    ))}
                    <div className="scroll-spacer" style={{ width: '15px', flexShrink: 0 }}></div>
                </div>

                {/* Trigger для подгрузки - невидимый */}
                {hasMore && (
                    <div
                        ref={loadMoreRef}
                        style={{ height: '1px', width: '100%' }}
                    />
                )}
            </div>
        </div>
    );
};

export default HomeScreen;
